interface QuotaAlert {
  id: number;
  provider: string;
  region: string;
  instance_family: string;
  alert_type: string;
  alert_level: 'warning' | 'critical' | 'emergency';
  message: string;
  is_active: boolean;
  is_acknowledged: boolean;
  created_at: string;
}

interface Props {
  alerts: QuotaAlert[];
  onAcknowledge: (alertId: number) => void;
}

export default function QuotaAlertPanel({ alerts, onAcknowledge }: Props) {
  const getAlertColor = (level: string) => {
    switch (level) {
      case 'emergency':
        return 'border-red-600 bg-red-900/20';
      case 'critical':
        return 'border-orange-600 bg-orange-900/20';
      case 'warning':
        return 'border-yellow-600 bg-yellow-900/20';
      default:
        return 'border-gray-600 bg-gray-900/20';
    }
  };

  const getAlertIcon = (level: string) => {
    switch (level) {
      case 'emergency':
        return (
          <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'critical':
        return (
          <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    return `${minutes}m ago`;
  };

  if (alerts.length === 0) {
    return null;
  }

  return (
    <div className="mb-8">
      <div className="flex items-center mb-4">
        <h2 className="text-xl font-semibold text-white">Active Alerts</h2>
        <span className="ml-2 px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full">
          {alerts.length} unacknowledged
        </span>
      </div>

      <div className="space-y-3">
        {alerts.map((alert) => (
          <div
            key={alert.id}
            className={`rounded-lg border p-4 ${getAlertColor(alert.alert_level)}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                <div className="mt-0.5">
                  {getAlertIcon(alert.alert_level)}
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className={`text-xs font-semibold uppercase px-2 py-0.5 rounded ${
                      alert.alert_level === 'emergency' ? 'bg-red-600 text-white' :
                      alert.alert_level === 'critical' ? 'bg-orange-600 text-white' :
                      'bg-yellow-600 text-white'
                    }`}>
                      {alert.alert_level}
                    </span>
                    <span className="text-gray-400 text-sm">
                      {alert.provider.toUpperCase()} • {alert.region} • {alert.instance_family}
                    </span>
                    <span className="text-gray-500 text-xs">
                      {formatTime(alert.created_at)}
                    </span>
                  </div>
                  <p className="text-white">
                    {alert.message}
                  </p>
                </div>
              </div>
              <button
                onClick={() => onAcknowledge(alert.id)}
                className="ml-4 px-3 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm rounded-lg transition-colors"
              >
                Acknowledge
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}